import { ClockCircleFilled, StarFilled } from '@ant-design/icons';
import { ArrowCircleDownIcon } from '@heroicons/react/solid';
import { Affix } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import DoubleTick from 'assets/Icons/DoubleTick';
import * as cookie from 'cookie';
import { AnimatePresence } from 'framer-motion';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Script from 'next/script';
import React, { useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { useSessionTracking } from '@/hooks/useSessionTracking';
import useTailwindBreakpoint from '@/hooks/useTailwindBreakpoint';

import RenderIf from '@/components/Common/RenderIf';
import CallbackModal from '@/components/CoursesV2/CallbackModal';

import { detectCountry, getActiveCountries } from '@/services/globalCountry';
import { setEventContext } from '@/utils/eventContext';
import { EventV2 } from '@/utils/Events/events';

import CourseCategoryIcon from '../../../../assets/Icons/CourseCategoryIcon';
import CourseDeviceIcon from '../../../../assets/Icons/CourseDeviceIcon';
import CourseLanguageIcon from '../../../../assets/Icons/CourseLanguageIcon';
import CourseModuleIcon from '../../../../assets/Icons/CourseModuleIcon';
import DiplomaIcon from '../../../../assets/Icons/DiplomaIcon';
import { tokenUpdate } from '../../../atoms/authModalControl';
import { dynamicTrackingParamsState } from '../../../atoms/dynamicTrackingParams';
import { localUserID, user } from '../../../atoms/user';
import RecommendedBooksSection from '../../../components/BookLandingPageComp/RecommendedBooksSection';
import CourseAuthorDetailsSection from '../../../components/CourseLandingPageComp/CourseAuthorDetailsSection';
import CourseCertificateSection from '../../../components/CourseLandingPageComp/CourseCertificateSection';
import CourseCurriculumSection from '../../../components/CourseLandingPageComp/CourseCurriculumSection';
import CourseDescription from '../../../components/CourseLandingPageComp/CourseDescription';
import CourseIncludesSection, {
  CourseIncludes,
} from '../../../components/CourseLandingPageComp/CourseIncludesSection';
import CourseInfo from '../../../components/CourseLandingPageComp/CourseInfo';
import CourseReviewSection from '../../../components/CourseLandingPageComp/CourseReviewSection';
import CoursePreviewModal from '../../../components/CourseLandingPageComp/CourseReviewSection/CoursePreviewModal';
import CourseSkillsSection from '../../../components/CourseLandingPageComp/CourseSkillsSection';
import HeroSectionCourse from '../../../components/CourseLandingPageComp/HeroSectionCourse';
import Requirements from '../../../components/CourseLandingPageComp/Requirements';
import ShareModal from '../../../components/CourseLandingPageComp/ShareModal';
import SlidCourseContainer from '../../../components/CourseLandingPageComp/SlidCourseContainer';
import Products from '../../../components/ProjectPage/Products';
import ButtonDefault, {
  ButtonVariants,
} from '../../../components/shared/basic/button';
import Section from '../../../components/shared/basic/section';
import { ShouldRender } from '../../../components/shared/basic/ShouldRender';
import { formateErrorObject } from '../../../components/shared/formateErrorObject';
import { getLoginToken } from '../../../components/shared/getToken';
import {
  extractAuthorDetailsData,
  extractHeroSectionCourseData,
  extractIBookDescriptionData,
  extractSlidCourseContainerData,
} from '../../../components/shared/LandingPageHelper';
import CustomLoader from '../../../components/shared/Loading';
import { notify } from '../../../components/shared/notify';
import { Endpoints } from '../../../network';
import { addToCart, addToRecentlyViewed } from '../../../services/Cart';
import {
  getCourseDataSSR,
  getCourseLandingPageData,
  getLandingPageSeoSSR,
  getProductDataSSR,
} from '../../../services/LandingPage';
import { getBookBySlug } from '../../../services/Library/Books';
import { addToRecentlyViewedProduct } from '../../../services/Library/RecentlyViewed';
import {
  checkIsPurchased,
  checkIsPurchasedSSR,
  getAllReviews,
} from '../../../services/ReviewLandingPage';
import { getAuthorProfileById } from '../../../services/School';
import { AddToCartProps } from '../../../utils/interfaces/cart';
import {
  IAuthorDetails,
  IAuthorDetailsList,
  IBookDescription,
  ICourseLandingPage,
  IHeroSectionCourseLandingPage,
  ISlidCourse,
} from '../../../utils/interfaces/landingPage';
import { IBookDetails } from '../../../utils/interfaces/Products/books';
import { User } from '../../../utils/interfaces/User';

interface Props {
  metaData: {
    title: string;
    description: string;
    author: string;
    keywords: string;
    image: string;
    openGraph: {
      title: string;
      description: string;
      image: string;
    };
    categories?: string;
    slug: string;
    price: number;
    reviews: any[];
    ratingValue: number;
    reviewCount: number;
  } | null;
  courseData: any;
  landingPageData: any;
  productData: any;
  isCoursePurchased?: boolean;
}

export const generateUrlForImage = (
  baseUrl: string,
  endpoint: string,
  id: string,
): string => {
  const encodedId = encodeURIComponent(id);
  const url = `${baseUrl}${endpoint}${encodedId}`;
  return url;
};

const CourseLandingPage: React.FC<Props> = ({
  metaData,
  courseData,
  landingPageData,
  productData,
  isCoursePurchased: initialCoursePurchased = false,
}) => {

  const twScreen = useTailwindBreakpoint();
  const [courseLandingDetails, setBookLandingDetails] =
    useState<ICourseLandingPage>();
  const [isPurchased, setIsPurchased] = useState<boolean>(
    initialCoursePurchased,
  );
  const [loading, setLoading] = useState(false);
  const [loadingAddToCart, setLoadingAddToCart] = useState({
    value: false,
    id: 0,
  });
  const [token, setToken] = useState('');
  const [offset, setOffset] = useState<number>(0);
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [correctedSlug, setCorrectedSlug] = useState<string>();

  const [bookDetails, setBooksDetails] = useState<IBookDetails>();
  const [isModalVisible, setIsModalVisible] = useState({
    value: false,
    name: '',
  });
  const [isScheduleCallModalVisible, setIsScheduleCallModalVisible] = useState({
    value: false,
    productId: 0,
  });
  const [isModalCoursePreviewVisible, setIsModalCoursePreviewVisible] =
    useState(false);

  const [totalReview, setTotalReview] = useState({
    total: 0,
    averageRating: 0,
  });
  const [isInViewport, setIsInViewport] = useState(false);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [lastFetchedBookId, setLastFetchedBookId] = useState<string | null>(
    null,
  );

  const closeModal = () =>
    setIsScheduleCallModalVisible({ value: false, productId: 0 });
  const openModal = (productId: number) =>
    setIsScheduleCallModalVisible({ value: true, productId: productId });
  const router = useRouter();
  const screens = useBreakpoint();
  const localUserId = useRecoilValue(localUserID);
  const userData: User | undefined = useRecoilValue(user);
  const setDynamicTrackingParams = useSetRecoilState(
    dynamicTrackingParamsState,
  );
  const [authorsData, setAuthorsData] = useState<{
    follower_count: string;
    user_name: string;
    authorId: string;
    isFollowed: boolean;
  }>();

  const { courses_slug, swp, short, id } = router.query;

  useEffect(() => {
    const checkVisibility = () => {
      if (!sectionRef.current) return;

      const rect = sectionRef.current.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

      setIsInViewport(isVisible);
    };

    window.addEventListener('scroll', checkVisibility);
    window.addEventListener('resize', checkVisibility);
    checkVisibility(); // Run on mount

    return () => {
      window.removeEventListener('scroll', checkVisibility);
      window.removeEventListener('resize', checkVisibility);
    };
  }, []);

  const getBookDetailsById = async (courses_slug: string) => {
    try {
      setLoading(true);
      const response = await getCourseLandingPageData(courses_slug);

      if (response?.data?.correctedSlug) {
        setCorrectedSlug(response?.data?.correctedSlug);
      }

      setBookLandingDetails({
        ...response?.data?.form,
        updatedAt: response?.data?.updatedAt,
      });

      if (response?.data?.bookType === 'short' && short !== 'true') {
        router.push(`${router.asPath.split('?')[0]}?short=true`, undefined, {
          shallow: true,
        });
      }
    } catch (error) {
      console.error('API call error:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAverageRating = (reviews: any[]) => {
    if (!Array.isArray(reviews) || reviews.length === 0) return null;

    const ratings = reviews
      .map((review) => review?.rating)
      .filter((rating) => typeof rating === 'number' && !isNaN(rating));

    if (ratings.length === 0) return null;

    const total = ratings.reduce((sum, rating) => sum + rating, 0);
    return parseFloat((total / ratings.length).toFixed(1));
  };
  const fetchReviewData = async (pageNum: number) => {
    try {
      const response = await getAllReviews({
        page: pageNum,
        limit: 9999,
        product_id: courseLandingDetails?.productId as string,
        ...(userData?.zl_uid && { zl_uid: userData.zl_uid }),
        type: 'course',
      });

      setTotalReview({
        total: response?.meta?.totalRecords || 0,
        averageRating: calculateAverageRating(response?.reviews || []) ?? 0,
      });
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    }
  };

  const getBookDetailsByIdSwp = async (
    courses_slug: string,
    userId?: string,
  ) => {
    try {
      setLoading(true);

      const response = await getBookBySlug(courses_slug, userId && userId);

      const formateData = {
        authorName: response?.authors?.[0],
        bookName: response?.title,
        desc: response?.description,
        lang: 'English',
        type: 'Course',
        _id: response?._id,
        authorAvatar: generateUrlForImage(
          `${process.env.NEXT_PUBLIC_API_READER_URL}`,
          `${Endpoints.getAuthorAvatar}/`,
          `${response?.author_list?.[0]?._id}`,
        ),
        totalPages: response?.totalPages,
        bookLSample: response?._id,
        bookType: response?.type,
        isSubscribed: response?.subscribe?.subscribed || false,
        image: generateUrlForImage(
          `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
          `${Endpoints.getCoverImagesForBooks}/`,
          `${response?._id}`,
        ),
      };
      setBooksDetails(formateData);
    } catch (error) {
      console.error('API call error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const updateOffset = () => {
      const containerHeight =
        document.querySelector('.slid-book-container')?.clientHeight || 0;
      const screenHeight = window.innerHeight;
      setOffset((screenHeight - containerHeight) / 10);
    };

    updateOffset();

    window.addEventListener('resize', updateOffset);

    return () => {
      window.removeEventListener('resize', updateOffset);
    };
  }, [courses_slug]);

  useEffect(() => {
    if (courses_slug && courses_slug !== lastFetchedBookId) {
      if (swp === 'false') {
        getBookDetailsByIdSwp(courses_slug as string);
      } else {
        getBookDetailsById(courses_slug as string);
      }

      setLastFetchedBookId(courses_slug as string);
    }
  }, [courses_slug, userData, token, short]);

  const fetchData = async () => {
    try {
      const token = await getLoginToken();
      setToken(token as string);
    } catch (error) {
      console.error('Error fetching token:', error);
    }
  };

  useEffect(() => {
    fetchData();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [isTokenChanged]);

  useEffect(() => {
    if (courseLandingDetails?.productId) {
      fetchReviewData(1);
    }
  }, [courseLandingDetails?.productId]);

  useEffect(() => {
    if (courseLandingDetails?.productId) {
      addToRecentlyViewedProduct(
        courseLandingDetails.productId,
        'course',
        userData?.zl_uid,
      );
    }
  }, [courseLandingDetails?.productId, userData?.zl_uid]);

  const checkIfPurchased = async () => {
    const targetProductId =
      courseLandingDetails?.productId ||
      landingPageData?.form?.productId ||
      (router.query.id && router.query.id !== 'undefined'
        ? router.query.id
        : null);

    if (!targetProductId) return;

    try {
      const data = await checkIsPurchased(`${targetProductId}` as string);
      setIsPurchased(Boolean(data));
    } catch (error: any) {
      console.error('Error checking course purchase status:', error);
    }
  };

  useEffect(() => {
    if (
      userData &&
      (courseLandingDetails?.productId ||
        landingPageData?.form?.productId ||
        router.query.id)
    ) {
      checkIfPurchased();
    }
  }, [
    userData,
    courseLandingDetails?.productId,
    landingPageData?.form?.productId,
    router.query.id,
    isTokenChanged,
    token,
  ]);

  useEffect(() => {
    const baseSlug = `/books/${courseLandingDetails?.productSlug}-${courseLandingDetails?.readersProductId}`;
    const queryParams = short === 'true' ? '?short=true' : '';
    const formattedSlug = `${baseSlug}${queryParams}`;

    if (
      correctedSlug &&
      courseLandingDetails?.readersProductId &&
      (router.pathname?.split('?')[0] || '') !== baseSlug
    ) {
      router.replace(formattedSlug, undefined, { shallow: true });
    }
  }, [correctedSlug, courseLandingDetails, short]);

  const handleAddDataToCart = async (
    payload: AddToCartProps,
    type: 'hardCopy' | 'digitalCopy',
  ) => {
    try {
      setLoadingAddToCart({ value: true, id: payload.id });

      await addToCart(payload);
      addToRecentlyViewed(payload.id, userData?.zl_uid);
      notify(
        `${slidCourseContainerData?.bookName} ${type === 'hardCopy' ? 'Hard Copy' : 'Digital Copy'}  added to cart successfully!`,
        'success',
      );
      router.push('/cart');
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoadingAddToCart({ value: false, id: payload.id });
    }
  };

  const heroSectionData: IHeroSectionCourseLandingPage | undefined =
    courseLandingDetails
      ? extractHeroSectionCourseData(courseLandingDetails)
      : undefined;

  const slidCourseContainerData: ISlidCourse | undefined = courseLandingDetails
    ? extractSlidCourseContainerData(courseLandingDetails)
    : undefined;

  const bookDescriptionData: IBookDescription | undefined = courseLandingDetails
    ? extractIBookDescriptionData(courseLandingDetails)
    : undefined;

  const courseIncludes: CourseIncludes[] = [
    ...(totalReview.averageRating
      ? [
        {
          icon: <StarFilled />,
          label: 'Rating',
          value: totalReview.averageRating?.toString(),
        },
      ]
      : []),
    { icon: <CourseLanguageIcon />, label: 'Language', value: 'English' },
    {
      icon: <ClockCircleFilled className='w-5 h-5' />,
      label: 'Length',
      value: courseLandingDetails?.courseDuration ?? 'N/A',
    },
    ...(courseLandingDetails?.modules?.length
      ? [
        {
          icon: <CourseCategoryIcon />,
          label: 'Category',
          value: courseLandingDetails?.category ?? '',
        },
      ]
      : []),

    ...(courseLandingDetails?.modules?.length
      ? [
        {
          icon: <CourseModuleIcon />,
          label: 'Lessons',
          value: `${courseLandingDetails?.modules?.length?.toString()} Modules`,
        },
      ]
      : []),

    {
      icon: <DiplomaIcon width='20' height='16' />,
      label: 'On Completion',
      value: 'Certificate',
    },
    {
      icon: <CourseDeviceIcon />,
      label: 'Access',
      value: 'Mobile and Desktop',
    },
  ];

  const courseInfoData = {
    uploadedDate: courseLandingDetails?.updatedAt as string,
    language: 'English',
    rating: totalReview?.averageRating ?? 0,
    totalRatings: totalReview?.total ?? 0,
    duration: courseLandingDetails?.courseDuration ?? 'N/A',
    courseLevel: courseLandingDetails?.courseLevel ?? 'intermediate',
  };

  const authorDetailsData: IAuthorDetails | IAuthorDetailsList[] | undefined =
    courseLandingDetails
      ? extractAuthorDetailsData(courseLandingDetails)
      : undefined;

  // const iframeData: IIframeLandingPage | undefined = courseLandingDetails
  //   ? extractIframeData(courseLandingDetails)
  //   : undefined;

  const handleGetAuthorProfileById = async (zl_uid: string) => {
    try {
      const data = await getAuthorProfileById({ zl_uid });
      setAuthorsData({
        user_name: data?.data?.profile_data?.user_name,
        follower_count: data?.data?.follower_count,
        authorId: data?.data?.zl_uid,
        isFollowed: data?.data.is_followed || false,
      });
    } catch (error: any) {
      null;
    }
  };

  useEffect(() => {
    if (heroSectionData?.authorZlUid) {
      handleGetAuthorProfileById(heroSectionData?.authorZlUid);
    }
  }, [heroSectionData?.authorZlUid]);

  useEffect(() => {
    if (productData?.display_name) {
      setDynamicTrackingParams({ source_name: productData.display_name });
    }
  }, [productData?.display_name, setDynamicTrackingParams]);


  useEffect(() => {
    if (landingPageData?.bookType) {
      setEventContext({ source_type: 'courses' });
    }
  }, [landingPageData.bookType])

  useEffect(() => {
    if (landingPageData?.categories?.[0]?.name) {
      setEventContext({ product_category: landingPageData?.categories?.[0]?.name });
    }
  }, [landingPageData?.categories?.[0]?.name])

  useEffect(() => {
    if (productData?.display_name) {
      setEventContext({ source_name: productData?.display_name });
    }
  }, [productData?.display_name]);

  useEffect(() => {
    const currentUrl = window.location.href;

    const utmData: Record<string, string> = {};
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.forEach((value, key) => {
      if (key.startsWith('utm_')) {
        utmData[key] = value;
      }
    });
    const zebra_selected_country = JSON.parse(localStorage.getItem('zebra_selected_country') as string)?.value?.country;

    const run = async () => {
      try {
        const sendProductView = () => {
          if (
            typeof nitro !== 'undefined' &&
            typeof nitro.productView === 'function'
          ) {
            const data = {
              title: productData?.display_name,
              image: productData?.thumbnail_image,
              page_url: currentUrl,
              product_id: productData?.id,
              product_category: landingPageData?.form?.category,
              product_type: productData?.category?.toLocaleLowerCase() === 'recorded course' ? 'course' : '',
              country: zebra_selected_country?.name,
              currency: productData?.i18n_attributes?.currency,
              utm_data: utmData,
            }
            nitro.productView(data);

          } else {
            console.warn('Nitro SDK not loaded');
          }
        };

        // Fire immediately if Nitro SDK is already ready
        if (
          typeof nitro !== 'undefined' &&
          typeof nitro.productView === 'function'
        ) {
          sendProductView();
        } else {
          // SDK not ready yet — chain into nitroSettings.ready so we don't miss the callback
          (window as any).nitroSettings = (window as any).nitroSettings || {};
          const existingReady = (window as any).nitroSettings.ready;

          (window as any).nitroSettings.ready = function (...args: any[]) {
            if (typeof existingReady === 'function') {
              existingReady.apply(this, args);
            }
            sendProductView();
          };
        }
      } catch (error) {
        console.error('Nitro Product View Error:', error);
      }
    };

    run();
  }, [])

  // Product Schema
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: metaData?.title,
    brand: {
      '@type': 'Brand',
      name: 'ZebraLearn',
    },
    image: metaData?.image || 'https://zebralearn.com/logo.png',
    description: metaData?.description,
    category: 'Books',
    review: metaData?.reviews.map((r: any) => ({
      '@type': 'Review',
      author: { '@type': 'Person', name: r?.user?.name ?? 'zebralearn' },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: r?.rating ?? 4,
        bestRating: 5,
      },
      reviewBody: r.text,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: (metaData?.reviews.length
        ? metaData.reviews.reduce(
          (sum: number, r: any) => sum + (r?.rating ?? 4),
          0,
        ) / metaData.reviews.length
        : 4
      ).toFixed(1),
      reviewCount: metaData?.reviews.length ?? 0,
      bestRating: Math.max(
        ...(metaData?.reviews.map((r: any) => r?.rating ?? 4) || [5]),
      ),
    },
  };



  // Events
  useEffect(() => {
    if (landingPageData?._id && userData && productData?.display_name) {
      EventV2({
        payload: {
          event_name: 'details_view',
          source: 'courses',
          source_type: 'courses',
          source_id: landingPageData?._id,
          source_name: productData?.display_name,
          unit: 'boolean',
          value: 1,
          meta: {
            product_category: landingPageData?.form?.tags?.[0]
          }
        },
        userData: userData,
        pathname: router.pathname
      })
    }

  }, [userData])

  // details_session event — tracks active seconds, fires automatically when user leaves
  useSessionTracking({
    enabled: !!userData,
    userData,
    payload: {
      event_name: 'details_session',
      source: 'courses',
      source_type: 'courses',
      source_id: landingPageData?._id,
      source_name: productData?.display_name,
      meta: {
        product_category: landingPageData?.categories?.[0]?.name,
      }
    }
  })

  return (
    <>
      <Head>
        <title>{metaData?.title}</title>

        <meta
          name='description'
          content={metaData?.description as string}
          key='description'
        />
        <meta name='keywords' content={metaData?.keywords as string} />
        <meta name='author' content={metaData?.author as string} />
        <meta itemProp='name' content='ZebraLearn' />
        <meta
          itemProp='description'
          content={metaData?.description as string}
        />
        <meta itemProp='image' content={metaData?.openGraph?.image as string} />
        <meta
          name='twitter:card'
          content={metaData?.openGraph?.description as string}
        />
        <meta name='twitter:site' content='ZebraLearn.com' />
        <meta
          name='twitter:title'
          content={metaData?.openGraph?.title as string}
        />
        <meta
          name='twitter:description'
          content={metaData?.openGraph?.description as string}
        />
        <meta name='twitter:creator' content={metaData?.author as string} />
        <meta
          name='twitter:image:src'
          content={metaData?.openGraph?.image as string}
        />
        <meta
          property='og:title'
          content={metaData?.openGraph?.title as string}
        />

        <meta property='og:type' content='website' />
        <meta property='og:url' content='https://zebralearn.com/' />
        <meta
          property='og:image'
          content={
            metaData?.openGraph?.image ||
            'https://zebralearn-store.s3.ap-south-1.amazonaws.com/thubnail-creative.png'
          }
        />
        <meta
          property='og:description'
          content={metaData?.description as string}
        />
        <meta property='og:site_name' content={metaData?.author as string} />
        <meta property='article:section' content='Website Section' />
        <meta property='article:tag' content={metaData?.keywords as string} />
      </Head>
      <Script
        id='product-schema'
        type='application/ld+json'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <ShouldRender
        check={!loading && courseLandingDetails && swp?.toString() !== 'false'}
      >
        <div className='relative flex h-full overflow-hidden '>
          <div className='w-full pb-20 md:pb-24 lg:pb-5'>
            <Section direction='left'>
              <div>
                <ShouldRender check={!screens.lg}>
                  <Section>
                    <div className='h-auto z-40'>
                      <SlidCourseContainer
                        landingPageData={landingPageData}
                        openModal={openModal}
                        isScheduleCallModalVisible={isScheduleCallModalVisible}
                        setIsScheduleCallModalVisible={
                          setIsScheduleCallModalVisible
                        }
                        slidCourseContainerData={
                          slidCourseContainerData as ISlidCourse
                        }
                        token={token}
                        openShare={() =>
                          setIsModalVisible({
                            value: true,
                            name: courseLandingDetails?.bookName as string,
                          })
                        }
                        openPreview={() => setIsModalCoursePreviewVisible(true)}
                        courseData={courseData}
                        isPurchased={isPurchased}
                      />
                    </div>
                  </Section>
                </ShouldRender>
                <div className=' bg-bgColor lg:bg-transparent md:py-4 lg:pb-[12%] xl:pb-[6%] lg:pt-[3%] px-4 md:px-10  lg:px-[6%] xl:px-[12.5%] 2xl:px-[13.5%] relative overflow-hidden'>
                  <div className='absolute  inset-0 hidden lg:block    backdrop-blur-2xl -z-10'></div>
                  <div className='absolute inset-0 hidden lg:block  bg-[#00000099]  -z-20'></div>

                  <div className='absolute inset-0 hidden lg:block   w-full h-full -z-30'>
                    <div className='relative inset-0 hidden lg:block   w-full h-full -z-30'>
                      <Image
                        src={courseLandingDetails?.bookImage as string}
                        alt='Background Image'
                        layout='fill'
                        priority
                        className='w-full h-full object-cover '
                      />
                    </div>
                  </div>

                  <ShouldRender check={!loading && heroSectionData}>
                    <div className='z-40'>
                      <HeroSectionCourse
                        landingPageData={landingPageData}
                        openModal={openModal}
                        authorsData={authorsData}
                        heroSectionData={
                          heroSectionData as IHeroSectionCourseLandingPage
                        }
                        slidCourseContainerData={slidCourseContainerData}
                        token={token}
                        sectionRef={sectionRef}
                        openShare={() =>
                          setIsModalVisible({
                            value: true,
                            name: courseLandingDetails?.bookName as string,
                          })
                        }
                        courseData={courseData}
                        isPurchased={isPurchased}
                      />
                    </div>
                  </ShouldRender>
                </div>

                <div className='lg:w-3/5  xl:w-2/3 lg:pt-[40px] lg:pb-[20px] py-[32px] lg:py-[40px] px-4 md:px-[6%] lg:pr-[4%] lg:pl-[6%] z-30 relative xl:pl-[12.5%]  2xl:pl-[13.5%] xl:pr-[8.5%] text-white flex flex-col gap-y-[32px]'>
                  <div className='lg:-mt-[25%] xl:-mt-[15%]'>
                    <CourseInfo data={courseInfoData} />
                  </div>

                  <CourseDescription
                    whatYouWillLearn={
                      bookDescriptionData?.whatYouWillLearn as string[]
                    }
                  />
                  <CourseSkillsSection
                    skills={courseLandingDetails?.skills as string[]}
                  />

                  <CourseCertificateSection />

                  <CourseIncludesSection data={courseIncludes} />
                  <ShouldRender check={courseLandingDetails?.modules?.length}>
                    <CourseCurriculumSection
                      data={courseLandingDetails?.modules || []}
                    />
                  </ShouldRender>
                  <Requirements
                    items={courseLandingDetails?.requirements || []}
                  />
                  <CourseAuthorDetailsSection
                    data={authorDetailsData as IAuthorDetails}
                    authorsData={authorsData}
                    token={token}
                    courseData={{
                      course_id: courseLandingDetails?.productId as string,
                      course_name: courseLandingDetails?.bookName as string,
                    }}
                  />
                  <RenderIf condition={!twScreen.sm}>
                    <div className='flex gap-y-3 flex-col'>
                      <div ref={sectionRef} className='flex flex-col '>
                        {isPurchased ? (
                          <ButtonDefault
                            size={4}
                            rounded={false}
                            roundedSm={true}
                            enableScaling={false}
                            onClick={(e) => {
                              e.stopPropagation();
                              router.push('/library/?tab=3');
                            }}
                            variant={ButtonVariants.PRIMARY}
                            className='flex w-full justify-center z-20 px-5 py-3 font-medium text-xs whitespace-nowrap'
                          >
                            <span className='px-4 text-sm md:text-base font-semibold py-1'>
                              Start Learning
                            </span>
                          </ButtonDefault>
                        ) : (
                          <>
                            <ButtonDefault
                              size={4}
                              rounded={false}
                              roundedSm={true}
                              enableScaling={false}
                              available={
                                courseData?.is_available &&
                                courseData?.i18n_attributes
                              }
                              loading={
                                Number(slidCourseContainerData?.productId) ===
                                loadingAddToCart.id && loadingAddToCart.value
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddDataToCart(
                                  {
                                    id: Number(
                                      slidCourseContainerData?.productId,
                                    ) as number,
                                    temp_user_id: localUserId,
                                    quantity: 1,
                                    trackingData: {
                                      productData: {
                                        price: Number(
                                          slidCourseContainerData?.main_price,
                                        ),
                                        id: Number(
                                          slidCourseContainerData?.productId,
                                        ),
                                        heading:
                                          slidCourseContainerData?.bookName as string,
                                        quantity: 1 as number,
                                        currency:
                                          courseData?.i18n_attributes?.currency,
                                        category:
                                          landingPageData?.form?.tags?.[0],
                                      },
                                      userData,
                                      pathname: router.pathname,
                                      productType: 'course',
                                      type: 'courses',
                                    },
                                  },
                                  'hardCopy',
                                );
                              }}
                              variant={ButtonVariants.PRIMARY}
                              className='flex w-full justify-center z-20  px-5 py-3 font-medium text-xs whitespace-nowrap'
                            >
                              <span className='px-4 text-sm md:text-base font-semibold py-1'>
                                Buy Now
                              </span>
                            </ButtonDefault>
                            <span className='flex bg-[#340905] gap-x-1 -translate-y-1 pt-2 font-medium text-xs text-[#FF7837] justify-center items-center pb-0.5 rounded-b-[8px]'>
                              <DoubleTick /> Lifetime Access
                            </span>
                          </>
                        )}
                      </div>

                      {!isPurchased && (
                        <ButtonDefault
                          size={4}
                          rounded={false}
                          roundedSm={true}
                          enableScaling={false}
                          onClick={() => {
                            openModal(Number(slidCourseContainerData?.productId));
                          }}
                          variant={ButtonVariants.WHITE}
                          className='flex w-full justify-center  px-5 py-3  border-none font-medium text-xs whitespace-nowrap'
                        >
                          <span className='px-4 text-sm md:text-base font-semibold py-1'>
                            Talk To Us
                          </span>
                        </ButtonDefault>
                      )}
                    </div>
                  </RenderIf>

                  <CourseReviewSection
                    isLoggedIn={!!token}
                    reviewOverview={courseLandingDetails?.reviewOverview}
                    productId={
                      courseLandingDetails?.productId?.toString() as string
                    }
                    authorName={authorDetailsData?.authorName as string}
                    courseImage={courseLandingDetails?.bookImage as string}
                    courseName={courseLandingDetails?.bookName as string}
                  />

                  <RecommendedBooksSection
                    category={metaData?.categories ?? undefined}
                    token={token}
                  />
                </div>
              </div>
            </Section>
          </div>
          <ShouldRender check={screens?.lg}>
            <AnimatePresence mode='wait'>
              <div className='absolute right-[6%] xl:right-[8vw] xl:top-[10vh]  '>
                <Affix offsetTop={offset}>
                  <Section direction='right'>
                    <div className='slid-book-container transition-all duration-300 ease-in-out'>
                      <SlidCourseContainer
                        landingPageData={landingPageData}
                        openModal={openModal}
                        isScheduleCallModalVisible={isScheduleCallModalVisible}
                        setIsScheduleCallModalVisible={
                          setIsScheduleCallModalVisible
                        }
                        slidCourseContainerData={
                          slidCourseContainerData as ISlidCourse
                        }
                        token={token}
                        openShare={() =>
                          setIsModalVisible({
                            value: true,
                            name: courseLandingDetails?.bookName as string,
                          })
                        }
                        openPreview={() => setIsModalCoursePreviewVisible(true)}
                        courseData={courseData}
                        isPurchased={isPurchased}
                      />
                    </div>
                  </Section>
                </Affix>
              </div>
            </AnimatePresence>
          </ShouldRender>

          <ShouldRender check={!screens.lg}>
            <div
              style={{
                position: 'fixed',
                left: 0,
                bottom: isInViewport ? '-100%' : '0',
                width: '100%',
                transition: 'bottom 0.5s ease-in-out, opacity 0.5s ease-in-out',
                opacity: isInViewport ? 0 : 1,
                zIndex: 50,
              }}
            >
              <div className='bg-[#131313CC] border-t border-[#1C1C1C] bg-opacity-75 px-2 xxs:px-4 md:px-10 lg:px-0 py-4 backdrop-blur-sm flex gap-x-4 xxs:gap-x-6 md:gap-x-8 pb-5 z-40'>
                {!isPurchased && (
                  <div>
                    <span className='flex xxxs:gap-x-1 md:gap-x-2 items-end pl-1'>
                      <ShouldRender
                        check={
                          courseData?.is_available && courseData?.i18n_attributes
                        }
                      >
                        <div className='flex flex-col'>
                          <p className='line-through text-[#FFFFFF99] text-sm md:text-base font-medium whitespace-nowrap'>
                            {courseData?.currency_symbol}{' '}
                            {slidCourseContainerData?.stroked_price}
                          </p>
                          <p className='text-xl font-semibold text-white whitespace-nowrap'>
                            {courseData?.currency_symbol}{' '}
                            {slidCourseContainerData?.main_price}
                          </p>
                        </div>
                        {slidCourseContainerData?.stroked_price &&
                          slidCourseContainerData?.main_price ? (
                          <p className='hidden xxxs:flex gap-x-1 pl-1 md:pl-0 text-[#16A34A] font-bold text-base items-center'>
                            {Math.round(
                              ((slidCourseContainerData.stroked_price -
                                Number(slidCourseContainerData.main_price)) /
                                slidCourseContainerData.stroked_price) *
                              100,
                            )}
                            %{' '}
                            <ArrowCircleDownIcon className='w-4 h-4 md:w-5 md:h-5' />
                          </p>
                        ) : null}
                      </ShouldRender>
                    </span>
                  </div>
                )}

                {isPurchased ? (
                  <ButtonDefault
                    size={3}
                    rounded={false}
                    isWidthFull={true}
                    roundedSm={true}
                    variant={ButtonVariants.PRIMARY}
                    enableScaling={false}
                    onClick={(e) => {
                      e.stopPropagation();
                      router.push('/library/?tab=3');
                    }}
                    className='w-full text-center text-base group'
                  >
                    Start Learning
                  </ButtonDefault>
                ) : (
                  <>
                    <RenderIf condition={!landingPageData?.form?.scheduleCall}>
                      <ButtonDefault
                        size={3}
                        rounded={false}
                        isWidthFull={true}
                        roundedSm={true}
                        available={
                          courseData?.is_available && courseData?.i18n_attributes
                        }
                        loading={
                          Number(slidCourseContainerData?.productId) ===
                          loadingAddToCart.id && loadingAddToCart.value
                        }
                        variant={ButtonVariants.PRIMARY}
                        enableScaling={false}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddDataToCart(
                            {
                              id: Number(courseLandingDetails?.productId) as number,
                              temp_user_id: localUserId,
                              quantity: 1,
                              trackingData: {
                                productData: {
                                  price: Number(courseLandingDetails?.main_price),
                                  id: Number(courseLandingDetails?.productId),
                                  heading: courseLandingDetails?.bookName as string,
                                  quantity: 1 as number,
                                  currency: courseData?.i18n_attributes?.currency,
                                  category: landingPageData?.form?.tags?.[0],
                                },
                                userData,
                                pathname: router.pathname,
                                productType: 'course',
                                type: 'courses',
                              },
                            },
                            'hardCopy',
                          );
                        }}
                        className='w-full text-center text-base group'
                      >
                        Buy Now
                      </ButtonDefault>
                    </RenderIf>
                    <RenderIf condition={landingPageData?.form?.scheduleCall}>
                      <ButtonDefault
                        size={3}
                        rounded={false}
                        isWidthFull={true}
                        roundedSm={true}
                        available={
                          courseData?.is_available && courseData?.i18n_attributes
                        }
                        enableScaling={false}
                        onClick={() => {
                          openModal(Number(slidCourseContainerData?.productId));
                        }}
                        variant={ButtonVariants.WHITE}
                        className='w-full text-center text-base font-semibold group'
                      >
                        Talk To Us
                      </ButtonDefault>
                    </RenderIf>
                  </>
                )}
              </div>
            </div>
          </ShouldRender>
        </div>
      </ShouldRender>

      <ShareModal
        open={isModalVisible.value}
        name={isModalVisible.name}
        onClose={() =>
          setIsModalVisible({
            value: false,
            name: courseLandingDetails?.bookName as string,
          })
        }
      />

      <CoursePreviewModal
        open={isModalCoursePreviewVisible}
        courseId={id as string}
        userData={userData}
        onClose={() => setIsModalCoursePreviewVisible(false)}
        courseSampleLink={courseLandingDetails?.courseSampleLink as string}
      />

      <CallbackModal
        visible={isScheduleCallModalVisible.value}
        onClose={closeModal}
        product={isScheduleCallModalVisible.productId}
        zl_uid={userData?.zl_uid}
      />
      <ShouldRender check={swp?.toString() === 'false'}>
        <Section>
          <div id='products' className={` flex items-center w-full `}>
            <ShouldRender check={!loading}>
              <Products productData={bookDetails} token={token} />
            </ShouldRender>
          </div>
        </Section>
      </ShouldRender>

      <ShouldRender check={loading}>
        <CustomLoader />
      </ShouldRender>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<Props> = async ({
  query,
  req,
}) => {
  try {
    const { courses_slug } = query;
    const rawCookies = req.headers.cookie || '';
    const parsedCookies = cookie.parse(rawCookies);

    let country = parsedCookies['country']?.trim();

    if (!country) {
      const [activeCountries, detectedCode] = await Promise.all([
        getActiveCountries(),
        detectCountry(req),
      ]);

      if (activeCountries.length > 0) {
        const fallback = activeCountries.find((c: any) => c.code === 'IN');
        const found =
          activeCountries.find((c: any) => c.code === detectedCode) || fallback;

        country = found?.code || 'IN';
      }
    }
    const landingPageData = await getCourseDataSSR(courses_slug as string);

    let metaData: any;
    if (landingPageData?.form?.productId) {
      metaData = await getLandingPageSeoSSR(
        landingPageData?.form?.productId || '',
      );
    }

    const formattedMetaData = {
      title: metaData?.title || '',
      description: metaData?.description || '',
      image:
        generateUrlForImage(
          `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
          `${Endpoints.getCoverImagesForBooks}/`,
          `${metaData?._id}`,
        ) || '',
      author: landingPageData?.form?.authorName || '',
      keywords: metaData?.keywords || '',
      openGraph: {
        title: metaData?.openGraph?.title ?? null,
        description: metaData?.openGraph?.description ?? null,
        image: metaData?.openGraph?.image ?? null,
      },
      categories: landingPageData?.form?.category ?? null,
      slug: landingPageData?.form?.productSlug ?? null,
      canonicalUrl: metaData?.canonicalUrl ?? 'https://zebralearn.com/',
      price: landingPageData?.form?.main_price ?? null,
      reviews: metaData?.reviews ?? [],
      ratingValue: landingPageData?.form?.rating ?? null,
      reviewCount: metaData?.reviews?.length ?? 0,
    };

    let courseData = null;
    let productData = null;
    if (landingPageData?.form?.productId) {
      productData = await getProductDataSSR(
        landingPageData?.form?.productId,
        country as string,
      );

      if (productData) {
        courseData = {
          stroked_price: productData?.stroked_price,
          main_price: productData?.main_price,
          currency_symbol: productData?.currency_symbol,
          is_available:
            (productData?.is_available && productData?.i18n_attributes) || null,
          is_preorder: productData?.is_preorder,
          i18n_attributes: productData?.i18n_attributes,
        };
      }
    }

    let isCoursePurchased = false;
    const token = parsedCookies['auth_token']?.trim();
    if (token && landingPageData?.form?.productId) {
      try {
        const checkPurchased = await checkIsPurchasedSSR(
          landingPageData.form.productId.toString(),
          token,
        );
        isCoursePurchased = Boolean(checkPurchased);
      } catch (error) {
        console.warn('Initial course access check failed:', error);
      }
    }

    return {
      props: {
        metaData: formattedMetaData,
        courseData: courseData,
        landingPageData: landingPageData,
        productData,
        isCoursePurchased,
      },
    };
  } catch (error) {
    notify('Error fetching metaData:', 'error');

    return {
      props: {
        metaData: null,
        courseData: null,
        landingPageData: null,
        productData: null,
      },
    };
  }
};
export default CourseLandingPage;
